import { useMsal } from "@azure/msal-react";
import SlAlert from "@shoelace-style/shoelace/dist/react/alert";
import SlButton from "@shoelace-style/shoelace/dist/react/button";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import SlInput from "@shoelace-style/shoelace/dist/react/input";
import SlSpinner from "@shoelace-style/shoelace/dist/react/spinner";
import { KeyboardEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LoginResponse } from "../../Api/authentication";
import { checkRole } from "../../Api/role";
import { useAppDispatch } from "../../app/store";
import logo from "../../assets/ditella-logo.png";
import { loginRequest } from "../../auth/authConfig";
import Translatable from "../Translatable/Translatable";
import { EstadoAlumnoCarrera } from "../../enums/alumnoCarrera.enum";
import { AppState } from "../../enums/appState.enum";
import { useAuth } from "../../hooks/useAuth";
import { showToastWarning, useToastError } from "../../hooks/useToast";
import { selectAdmin, updateAdminData } from "../../reduxSlices/adminSlice";
import { selectMobileFeatures, selectTabletDevice } from "../../reduxSlices/globalFlagsSlice";
import { selectParameter, updateParametersData } from "../../reduxSlices/parametersSlice";
import { doLoginThunk, selectStudent } from "../../reduxSlices/studentSlice";
import "./Login.css";
const pjson = require("../../../package.json");

const Login = () => {
  const [email, setEmail] = useState("");
  const {
    isAuthenticating,
    multicarrera,
    setIsAuthenticating,
    setMulticarrera,
    setCarrerasList,
    setInicializoConMultiCarrera,
    handleAuthResponse,
  } = useAuth();

  const { instance, accounts } = useMsal();
  const { appState } = useSelector(selectParameter);
  const { role } = useSelector(selectAdmin);
  const student = useSelector(selectStudent);
  const isMobile = useSelector(selectMobileFeatures);
  const isTablet = useSelector(selectTabletDevice);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const showError = useToastError();

  const version = `-v${pjson.version}`;

  useEffect(() => {
    const token = localStorage.getItem("token");
    const idAP = localStorage.getItem("idAP");

    if (!token) {
      localStorage.clear();
      return;
    }

    if (token && !multicarrera && idAP) {
      navigate("/");
    }
  }, [dispatch, navigate, multicarrera]);

  function MicrosoftLogin() {
    const loginMS = async () => {
      localStorage.setItem("MSlogin", "1");
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error(error);
      }
    };
    loginMS();
  }

  const clearTokenAndMicrosoftCache = async () => {
    localStorage.clear();
    instance.clearCache();
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!student.id || !student.studentInitState || !token || !student.listadoCarreras?.length) {
      return;
    }

    if (student.studentInitState === EstadoAlumnoCarrera.INICIALIZACION_KV_PENDIENTE) {
      return;
    }

    if (student.listadoCarreras.length === 1) {
      navigate("/");
    }
  }, [student, navigate, setCarrerasList, setInicializoConMultiCarrera, setMulticarrera]);

  useEffect(() => {
    const existingToken = localStorage.getItem("token");
    const idAP = localStorage.getItem("idAP");
    // Sesión ya establecida: no re-ejecutar doLogin por accounts MSAL
    if (existingToken && idAP) {
      return;
    }

    const actualUser = accounts[0]?.username;

    if (actualUser && !multicarrera) {
      console.log(`-> Microsoft accounts: ${JSON.stringify(accounts)}`);
      checkRole(actualUser).then((res) => {
        if (res === "admin") {
          dispatch(updateAdminData({ name: actualUser, role: res }));

          dispatch(updateParametersData({ appState: AppState.BY_PASS, started: true }));
        } else {
          if (appState === AppState.BY_PASS_SOPORTE) {
            clearTokenAndMicrosoftCache();
            showToastWarning("Aplicación no disponible, por favor intente nuevamente mas tarde.");
          } else {
            handleLoginGetToken(actualUser);
          }
        }
      });
    }
  }, [accounts, appState, dispatch, handleAuthResponse, multicarrera]);

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleInput = (e: any) => {
    if (e?.target?.value) {
      setEmail(e?.target?.value);
    }
  };

  const handleLoginGetToken = async (email: string) => {
    setIsAuthenticating(true);

    try {
      const result = await dispatch(
        doLoginThunk({
          email,
          // 2 reintentos: el primero espera 3s, el segundo 1.5s.
          maxRetries: 2,
          retryDelays: [3000, 1500],
        }),
      ).unwrap();

      const response: LoginResponse = { data: result.data, status: result.status };
      handleAuthResponse(response.data);
    } catch (err) {
      console.error("Login failed:", err);
      showError("Error al iniciar sesión. Por favor intente nuevamente.");
    } finally {
      setIsAuthenticating(false);
    }
  };



  return (
    <>
      <div className="page">
        <img src={logo} alt="logo Ditella" width={150} />
        {appState === "0" && role === "user" && accounts[0]?.username && !multicarrera && (
          <SlSpinner
            style={{
              fontSize: "3rem",
              //@ts-ignore
              "--indicator-color": "#00C4B3",
              "--track-color": "#ccebe8",
            }}
          />
        )}
        {(appState === "0" || appState === "4") && role === "user" && !accounts[0]?.username && !multicarrera && (
          <>
            <p>
              <Translatable as="span">
                Para realizar la inscripción deberá ingresar al sistema utilizando su mail de la universidad.
              </Translatable>
            </p>
            <p>
              <Translatable as="span">Identifíquese usando su cuenta.</Translatable>
            </p>

            <button
              onClick={MicrosoftLogin}
              className="rounded-[4px] flex gap-4 items-center p-2 px-6 bg-[#424242] text-white hover:bg-black"
            >
              <SlIcon name="microsoft"></SlIcon> <Translatable as="span" capitalize>Ingreso con Microsoft</Translatable>
            </button>

            {(isMobile || isTablet) && (
              <SlAlert variant="primary" open className="recommendation-box">
                <SlIcon slot="icon" name="laptop"></SlIcon>
                Para una mejor experiencia por favor usa un dispositivo de escritorio.
              </SlAlert>
            )}

            <p>
              <small>{version.split("v")[1]}</small>
            </p>
          </>
        )}
        {(appState === "2" || role === "admin") && !multicarrera && (
          <form className="login-form" onKeyDown={handleKeyDown}>
            <div className="row">
              <SlInput className="input-test" name="email" value={email} onSlInput={handleInput} required />
              <SlButton
                size="medium"
                type="button"
                onClick={() => handleLoginGetToken(email)}
                disabled={isAuthenticating}
                loading={isAuthenticating}
              >
                <SlIcon slot="prefix" name="box-arrow-in-right"></SlIcon>
              </SlButton>
            </div>
            {(isMobile || isTablet) && (
              <SlAlert variant="primary" open className="recommendation-box">
                <SlIcon slot="icon" name="laptop"></SlIcon>
                Para una mejor experiencia por favor usa un dispositivo de escritorio.
              </SlAlert>
            )}
          </form>
        )}
      </div>
    </>
  );
};

export default Login;
